import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import classNames from 'classnames'
import {memo} from 'react'

import BuildsFetcher from '../../../../../../../../containers/BuildsFetcher'
import EntityPath from '../../../../../../../../containers/EntityPath'
import {useIsAutodetectTimeZone} from '../../../../../../../../hooks/useIsAutodetectTimeZone'
import {getBuild} from '../../../../../../../../selectors'
import {stringifyId} from '../../../../../../../../types'
import {parseBranch} from '../../../../../../../../utils/branchNames'
import {
  displayDate,
  formatDurationWithMilliseconds,
} from '../../../../../../../../utils/dateTimeUtils'
import AgentInfo from '../../../../../../../common/AgentInfo/AgentInfo.container'
import BuildBranch from '../../../../../../../common/BranchLabel/BuildBranchLabel.container'
import BuildNumberContainer from '../../../../../../../common/BuildNumber/BuildNumber.container'
import BuildStatus from '../../../../../../../common/BuildStatus/BuildStatus.container'
import Popup from '../../../../../../../common/Popup/Popup.lazy'
import TestStatus from '../../../../../TestStatus/TestStatus'

import type {Props} from './BarPopup.types'

import styles from './BarPopup.css'

import {useAppSelector} from 'src/hooks/react-redux'

export const POPUP_DIRECTIONS = [Directions.TOP_RIGHT, Directions.TOP_LEFT]

function BarPopup({anchor, buildId, duration, testOccurrenceId}: Props) {
  const build = useAppSelector(state => getBuild(state, buildId))
  const isAutodetectTimeZone = useIsAutodetectTimeZone()
  const {buildType: buildTypeId, startDate, branchName} = build ?? {}
  const branch = parseBranch(branchName)
  return buildId != null && testOccurrenceId != null ? (
    <Dropdown key={stringifyId(buildId)} className={styles.wrapper} anchor={anchor} hoverMode>
      <Popup directions={POPUP_DIRECTIONS} className={styles.popup} autoCorrectTopOverflow={false}>
        <BuildsFetcher locator={`id:${stringifyId(buildId)}`} withRunning>
          <div
            className={classNames(styles.content, {
              [styles.loading]: build == null,
            })}
          >
            <div>{'Test duration'}</div>
            <div className={styles.durationValue}>{formatDurationWithMilliseconds(duration)}</div>
            <div className={styles.statusHeading}>{'Status'}</div>
            <div>
              <TestStatus testOccurrenceId={testOccurrenceId} />
            </div>
            {build != null && (
              <>
                <div>{'Build'}</div>
                <div className={styles.buildValue}>
                  <BuildNumberContainer className={styles.buildNumber} buildId={buildId} />
                  <BuildStatus buildId={buildId} />
                </div>
                <div>{'Configuration'}</div>
                <div>
                  <EntityPath
                    linkClassName={styles.buildPathLinks}
                    buildTypeId={buildTypeId}
                    projectOrBuildTypeNode={{
                      nodeType: 'all',
                    }}
                    withCollapsing
                  />
                </div>
                {branch != null && (
                  <>
                    <div>{'Branch'}</div>
                    <div>
                      <BuildBranch buildId={buildId} defaultTrim />
                    </div>
                  </>
                )}
                <div>{'Agent'}</div>
                <div>
                  <AgentInfo buildId={buildId} />
                </div>
                {startDate != null && (
                  <>
                    <div>{'Started'}</div>
                    <div>{displayDate(startDate, isAutodetectTimeZone, true)}</div>
                  </>
                )}
              </>
            )}
          </div>
          {build == null && <LoaderInline>{'Loading build details...'}</LoaderInline>}
        </BuildsFetcher>
      </Popup>
    </Dropdown>
  ) : null
}

export default memo<Props>(BarPopup)
